<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useMeta } from 'vue-meta';
import axios from 'axios';

import { ENV } from '../../../config/app-env';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import i18n from '@/plugins/i18n';

import Checkbox from 'primevue/checkbox';
import Divider from 'primevue/divider';
import DSButton from 'primevue/button';
import ChangeEmailModal from './components/ChangeEmailModal.vue';
import SignOutModal from './components/SignOutModal.vue';
import { RequestStatus } from '@/types/request.types';
import { useToast } from '@/composables/useToast';
import { isAuthenticatedWithGoogle } from '@/utils/auth0';

interface Auth0PasswordResetRequest {
  client_id: string;
  email: string;
  connection: string;
}

useMeta({
  title: 'Account Settings | Clearco',
});

const store = useStore();
const route = useRoute();
const toast = useToast();

// State
const passwordRequestComplete = ref<boolean>(false);
const agreedToNewsletter = ref<boolean>(false);
const changeEmailModalVisible = ref<boolean>(false);
const signOutModalVisible = ref<boolean>(false);

// Computed
const user = computed<UserResponseDto>(() => store.getters.user);

const updateUserRequest = computed<RequestStatus>(() => {
  return store.getters.requestStatus('UPDATE_USER');
});

const disableChangeEmailButton = computed<boolean>(() => {
  return isAuthenticatedWithGoogle();
});

// Methods
const trackWayfindingUserClickEvent = (
  name: SEGMENT_EVENTS,
  additionalProps: Record<string, any> = {},
): void => {
  analytics.track(name, {
    targetArea: route.name,
    ...additionalProps,
  });
};

const showEmailModal = (): void => {
  trackWayfindingUserClickEvent(SEGMENT_EVENTS.fe_change_email_clicked);
  changeEmailModalVisible.value = true;
};

const handleEmailUpdated = (newEmail: string): void => {
  toast.success(`Email successfully updated to ${newEmail}`, {
    summary: 'Success',
  });
};

const initiateChangePassword = async (): Promise<void> => {
  trackWayfindingUserClickEvent(SEGMENT_EVENTS.fe_change_password_clicked);

  const data: Auth0PasswordResetRequest = {
    client_id: ENV.AUTH0_CLIENT_ID ?? '',
    email: user.value.email,
    connection: 'Username-Password-Authentication',
  };

  try {
    await axios.post(
      `https://${ENV.AUTH0_DOMAIN}/dbconnections/change_password`,
      data,
    );
    passwordRequestComplete.value = true;
    toast.success(
      i18n.t('account.pages.subpageSecurity.checkYourEmail') as string,
      {
        summary: 'Success',
      },
    );
  } catch (_error) {
    toast.error(
      'An error occurred while trying to reset your password. Please try again or contact customer support',
    );
  }
};

const showLogoutModal = (): void => {
  trackWayfindingUserClickEvent(SEGMENT_EVENTS.fe_sign_out_clicked);
  signOutModalVisible.value = true;
};

const handleMarketingEmailsChange = async (): Promise<void> => {
  await store.dispatchApiAction('UPDATE_USER', {
    agreedToNewsletter: agreedToNewsletter.value,
  });

  trackWayfindingUserClickEvent(SEGMENT_EVENTS.if_account_settings_updated, {
    agreedToNewsletter: agreedToNewsletter.value,
    email: user.value.email,
  });

  toast.success(`Email marketing preferences updated.`, {
    summary: 'Success',
  });
};

// Lifecycle
onMounted(() => {
  agreedToNewsletter.value = user.value.agreedToNewsletter ?? false;
});
</script>

<template>
  <div>
    <header :class="$style['header-title']">Account Settings</header>
    <Divider :class="$style.divider" />
    <i18n-t keypath="account.pages.subpageSecurity.yourEmailIs">
      <template #email>
        <b>{{ user.email }}</b>
      </template>
    </i18n-t>
    <Divider :class="$style.divider" />
    <template v-if="user.isPrimary">
      <Checkbox
        v-model="agreedToNewsletter"
        binary
        input-id="agreedToNewsletter"
        :disabled="updateUserRequest.isPending"
        @change="handleMarketingEmailsChange"
      />
      <label for="agreedToNewsletter" class="m-l-10">
        Receive marketing emails
      </label>
      <Divider :class="$style.divider" />
    </template>
    <div :class="$style['buttons-container']">
      <span
        style="width: 100%"
        v-tooltip="
          disableChangeEmailButton ? $t('common.changeEmailNotPermitted') : null
        "
      >
        <DSButton
          @click="showEmailModal"
          :disabled="disableChangeEmailButton"
          :label="$t('common.changeEmail')"
          :class="[$style.button, 'p-button-outlined']"
        />
      </span>
      <DSButton
        :disabled="passwordRequestComplete"
        @click="initiateChangePassword"
        label="Reset Password"
        :class="[$style.button, 'p-button-outlined']"
      />
      <DSButton
        @click="showLogoutModal"
        :label="$t('common.signOut')"
        :class="[$style.button, 'p-button-danger']"
      />
    </div>

    <ChangeEmailModal
      v-model="changeEmailModalVisible"
      @email-updated="handleEmailUpdated"
    />

    <SignOutModal v-model="signOutModalVisible" />
  </div>
</template>

<style lang="less" module>
.header-title {
  font-family: @regular-font;
  font-weight: 700;
  font-size: 21px;
  margin: 30px 0;
}

.button {
  width: 100%;
}

.buttons-container {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: 24px;

  @media only screen and (max-width: 500px) {
    flex-direction: column;
  }
}

.divider {
  &::before {
    border-color: rgba(0, 0, 0, 0.06) !important;
  }
}
</style>
